//
// # Instance Base Context Menu
//
// Intermediate abstract class for instance context menus (layout and schematic).
// Provides shared action handlers, multi-select content, and menu item handler
// wiring that is identical across both instance menu types.
//

import type { Editor } from "./editor";
import type { schema } from "GenAlphaSchema";
import { logger } from "CoreUtils";
import { Palette } from "DrawCore/src/style";
import { BaseContextMenu } from "./baseContextMenu";
import type { ContextMenuStyleConfig } from "./baseContextMenu";

export abstract class InstanceBaseContextMenu extends BaseContextMenu {
  // The target ident of the right-clicked instance (for "Open In New Tab", "View in Depot")
  protected currentTargetIdent: schema.Ident | null = null;
  // The linked schematic(s) of the right-clicked instance's symbol, if any
  // (drives the "Open Schematic" action). Null when the target has no symbol
  // or the symbol has no schematic implementation.
  protected currentSchematicImpls: ReadonlyArray<schema.symbol.SchematicImplRef> | null =
    null;
  // The pdkId of the right-clicked instance's symbol, used to filter the
  // schematic picker when there is more than one linked schematic.
  protected currentSymbolPdkId: string | undefined = undefined;
  // How many items are selected (drives single vs multi-select menu display)
  protected selectionSize: number = 1;

  constructor(editor: Editor) {
    super(editor);
  }

  // -- Style config shared by both instance menus --

  readonly styleConfig: ContextMenuStyleConfig = {
    minWidth: "250px",
    maxWidth: "400px",
    overflow: "hidden",
  };

  // -- Subclass stylesheet: instance-specific CSS rules --

  protected getSubclassStyleSheet(): string {
    const p = Palette.getAll();
    return `
      .context-menu-header {
        font-weight: bold;
        margin-bottom: 4px;
        color: ${p.textHeader};
        overflow-wrap: break-word;
        word-break: break-word;
      }
      .context-menu-target {
        color: ${p.textTarget};
        margin-bottom: 4px;
        font-size: 11px;
        overflow-wrap: break-word;
        word-break: break-word;
      }
      .context-menu-position {
        color: ${p.textTarget};
        margin-bottom: 4px;
        font-size: 11px;
        overflow-wrap: break-word;
        word-break: break-word;
      }
      .context-menu-section {
        margin-top: 8px;
      }
      .context-menu-section-header {
        font-weight: bold;
        margin-bottom: 4px;
        color: ${p.textSection};
        font-size: 11px;
      }
      .context-menu-param {
        margin-left: 4px;
        font-size: 11px;
        color: ${p.textParam};
        font-family: 'Monaco', 'Courier New', monospace;
        overflow-wrap: break-word;
        word-break: break-word;
      }
    `;
  }

  // -- Shared instance menu content --

  // Build content for multi-select case
  protected buildMultiSelectContent = (): string => {
    let content = `<div class="context-menu-info">`;
    content += `<div class="context-menu-header">${this.selectionSize} Items Selected</div>`;
    content += `</div>`;

    // Add separator
    content += `<div class="context-menu-separator"></div>`;

    // Only bulk actions: Duplicate and Delete
    content += `<div class="context-menu-actions">`;
    content += `<div class="context-menu-item" data-action="duplicate">Duplicate</div>`;
    content += `<div class="context-menu-item" data-action="delete">Delete</div>`;
    content += `</div>`;

    return content;
  };

  // -- Shared menu item handler wiring --

  // Set up click handlers for menu items
  protected setupInstanceMenuItemHandlers = (): void => {
    if (!this.menuElement) return;

    const menuItems = this.menuElement.querySelectorAll(".context-menu-item");
    menuItems.forEach((item) => {
      const element = item as HTMLElement;
      element.onclick = (e) => {
        e.stopPropagation();
        const action = element.getAttribute("data-action");

        if (action === "open-target") {
          this.handleOpenInstanceTarget();
        } else if (action === "open-schematic") {
          this.handleOpenSchematic();
        } else if (action === "view-in-depot") {
          this.handleViewInDepot();
        } else if (action === "edit-params") {
          const instanceId = element.getAttribute("data-instance-id");
          if (instanceId) {
            this.handleEditParameters(instanceId);
          }
        } else if (action === "add-dummy-devices") {
          const instanceId = element.getAttribute("data-instance-id");
          const instanceName =
            element.getAttribute("data-instance-name") ??
            `instance ${instanceId}`;
          if (instanceId) {
            this.handleAddDummyDevices(Number(instanceId), instanceName);
          }
        } else if (action === "rename") {
          const instanceId = element.getAttribute("data-instance-id");
          if (instanceId) {
            this.handleRenameInstance(instanceId);
          }
        } else if (action === "duplicate") {
          this.handleDuplicateSelected();
        } else if (action === "delete") {
          this.handleDeleteSelected();
        } else if (action === "show-in-hierarchy") {
          const instName = element.getAttribute("data-instance-name");
          const instId = element.getAttribute("data-instance-id");
          if (instName && instId) {
            this.editor.platform.notifyShowInHierarchy(
              instName,
              Number(instId)
            );
          }
        }

        this.hide();
      };
    });
  };

  // -- Shared action handlers --

  // Handle add dummy devices action
  private handleAddDummyDevices = (
    instanceId: number,
    instanceName: string
  ): void => {
    this.editor.platform.notifyAddDummyDevices([instanceId], [instanceName]);
  };

  // Handle edit parameters action
  private handleEditParameters = (instanceId: string): void => {
    // Notify platform to trigger editInstanceParameters command
    this.editor.platform.notifyEditInstanceParameters(instanceId);
  };

  // Handle rename instance action
  private handleRenameInstance = (instanceId: string): void => {
    this.editor.platform.notifyRenameInstance(instanceId);
  };

  // Handle duplicate action - duplicates all selected instances
  private handleDuplicateSelected = (): void => {
    const count = this.editor.content.selection.size;
    this.editor.content.duplicateSelected();
    if (count > 0) this.editor.showItemFeedback("Duplicated", count);
  };

  // Handle view in depot action - reveal instance's target in the Depot tree
  private handleViewInDepot = (): void => {
    if (!this.currentTargetIdent) {
      logger.error("Cannot view in depot: no target ident available");
      return;
    }
    this.editor.platform.notifyViewInDepot(this.currentTargetIdent);
  };

  // Handle open schematic action - navigate to the schematic(s) implementing
  // this instance's target symbol. No-op when the symbol has no linked
  // schematic (the menu item is rendered disabled in that case).
  private handleOpenSchematic = (): void => {
    if (
      !this.currentSchematicImpls ||
      this.currentSchematicImpls.length === 0
    ) {
      return;
    }
    this.editor.platform.notifyOpenSchematicForInstance(
      this.currentSchematicImpls,
      this.currentSymbolPdkId
    );
  };

  // Handle open instance target in new tab action
  private handleOpenInstanceTarget = (): void => {
    if (!this.currentTargetIdent) {
      logger.error("Cannot open target: no target ident available");
      return;
    }

    // Notify platform to open the target design in a new tab
    this.editor.platform.notifyOpenInstanceTarget(this.currentTargetIdent);
  };

  // Handle delete action - deletes all selected instances
  private handleDeleteSelected = (): void => {
    const count = this.editor.content.selection.size;
    this.editor.content.deleteSelected();
    if (count > 0) this.editor.showItemFeedback("Deleted", count);
  };

  // -- clearState: nulls shared instance fields, delegates to subclass --

  protected clearState(): void {
    this.currentTargetIdent = null;
    this.currentSchematicImpls = null;
    this.currentSymbolPdkId = undefined;
    this.clearInstanceState();
  }

  // Subclass hook to null out the subclass-specific instance field.
  protected abstract clearInstanceState(): void;
}
